import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSearch } from '@fortawesome/free-solid-svg-icons';
import styles from './Dropdown.module.css';

// Context to share dropdown state with subcomponents
const DropdownContext = createContext({
  closeDropdown: () => {},
});

/**
 * Dropdown Root Component
 * Handles the trigger wrapper, portal/dropdown wrapper, and animations.
 */
export const Dropdown = ({ 
  trigger, 
  children, 
  align = 'left', // 'left' | 'right' | 'center'
  width = 'auto',
  className = '',
  menuClassName = '',
  searchable = false,
  searchPlaceholder = 'بحث...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
    if (isOpen) setSearchQuery('');
  };
  
  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeDropdown();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current.focus(), 50);
    }
  }, [isOpen, searchable]);

  // Alignment Logic
  const alignStyles = {
    left: { left: 0 },
    right: { right: 0 },
    center: { left: '50%', transform: 'translateX(-50%)' },
  };

  // Filter children
  const extractText = (node) => {
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(extractText).join('');
    if (React.isValidElement(node)) return extractText(node.props.children);
    return '';
  };

  let filteredChildren = React.Children.toArray(children).filter(child => {
    if (!searchable || !searchQuery) return true;
    if (React.isValidElement(child)) {
      if (child.type === DropdownDivider) return false;
      const text = child.props.searchableText || extractText(child.props.children);
      return text.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  if (searchable && !searchQuery) {
    filteredChildren = filteredChildren.slice(0, 5);
  }

  return (
    <DropdownContext.Provider value={{ closeDropdown }}>
      <div 
        className={`${styles.dropdownContainer} ${className}`} 
        ref={dropdownRef}
        style={width !== 'auto' ? { width } : undefined}
      >
        <div 
          className={styles.triggerWrapper} 
          onClick={toggleDropdown} 
          role="button" 
          aria-haspopup="menu" 
          aria-expanded={isOpen}
          style={width !== 'auto' ? { width: '100%' } : undefined}
        >
          {trigger}
        </div>
        
        <AnimatePresence>
          {isOpen && [
            <motion.div 
              key="backdrop"
              className={styles.mobileBackdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeDropdown}
            />,
            <motion.div
              key="menu"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className={`${styles.dropdownMenu} ${menuClassName}`}
              style={{ ...alignStyles[align], width }}
              role="menu"
            >
              <div className={styles.menuContent}>
                {searchable && (
                  <div className={styles.searchContainer}>
                    <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                    <input
                      ref={searchInputRef}
                      type="text"
                      className={styles.searchInput}
                      placeholder={searchPlaceholder}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                {filteredChildren.length > 0 ? (
                  filteredChildren
                ) : (
                  <div className={styles.noResults}>لا توجد نتائج</div>
                )}
              </div>
            </motion.div>
          ]}
        </AnimatePresence>
      </div>
    </DropdownContext.Provider>
  );
};

/**
 * Standard Text/Icon Item
 */
export const DropdownItem = ({ 
  children, 
  icon, 
  trailingIcon,
  onClick, 
  className = '', 
  disabled = false,
  danger = false,
  closeOnClick = true
}) => {
  const { closeDropdown } = useContext(DropdownContext);

  const handleClick = (e) => {
    if (disabled) return;
    onClick?.(e);
    if (closeOnClick) closeDropdown();
  };

  return (
    <div 
      className={`
        ${styles.dropdownItem} 
        ${disabled ? styles.disabled : ''} 
        ${danger ? styles.danger : ''} 
        ${className}
      `}
      onClick={handleClick}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
    >
      {icon && <span className={styles.itemIcon}>{icon}</span>}
      <span className={styles.itemContent}>{children}</span>
      {trailingIcon && <span className={styles.trailingIcon}>{trailingIcon}</span>}
    </div>
  );
};

/**
 * Checkbox Item (for Multi-Selection)
 * Note: Does not close dropdown on click by default
 */
export const DropdownCheckboxItem = ({
  children,
  checked,
  onChange,
  icon,
  className = '',
  disabled = false,
}) => {
  const handleClick = (e) => {
    if (disabled) return;
    onChange?.(!checked);
  };

  return (
    <div 
      className={`
        ${styles.dropdownItem} 
        ${styles.checkboxItem} 
        ${disabled ? styles.disabled : ''} 
        ${className}
      `}
      onClick={handleClick}
      role="menuitemcheckbox"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
    >
      <div className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ''}`}>
        {checked && <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />}
      </div>
      {icon && <span className={styles.itemIcon}>{icon}</span>}
      <span className={styles.itemContent}>{children}</span>
    </div>
  );
};

/**
 * Switch/Toggle Item
 * Note: Does not close dropdown on click by default
 */
export const DropdownSwitchItem = ({
  children,
  checked,
  onChange,
  icon,
  className = '',
  disabled = false,
}) => {
  const handleClick = (e) => {
    if (disabled) return;
    onChange?.(!checked);
  };

  return (
    <div 
      className={`
        ${styles.dropdownItem} 
        ${styles.switchItem} 
        ${disabled ? styles.disabled : ''} 
        ${className}
      `}
      onClick={handleClick}
      role="menuitemcheckbox"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
    >
      {icon && <span className={styles.itemIcon}>{icon}</span>}
      <span className={styles.itemContent}>{children}</span>
      <div className={`${styles.switch} ${checked ? styles.switchChecked : ''}`}>
        <motion.div 
          className={styles.switchHandle}
          layout
          transition={{ type: "spring", stiffness: 700, damping: 30 }}
        />
      </div>
    </div>
  );
};

/**
 * Dropdown Section Label
 */
export const DropdownLabel = ({ children, className = '' }) => (
  <div className={`${styles.dropdownLabel} ${className}`}>
    {children}
  </div>
);

/**
 * Divider for separating groups
 */
export const DropdownDivider = ({ className = '' }) => (
  <div className={`${styles.dropdownDivider} ${className}`} role="separator" />
);

// Export all components from default for convenience
const DropdownComponents = {
  Root: Dropdown,
  Item: DropdownItem,
  CheckboxItem: DropdownCheckboxItem,
  SwitchItem: DropdownSwitchItem,
  Label: DropdownLabel,
  Divider: DropdownDivider
};

export default DropdownComponents;
